import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAccount, useChainId } from 'wagmi'

import { Hash, Token } from 'types/entities'

import { useRequest } from 'hooks/api'
import { useApproveERC20 } from 'hooks/token'
import { useNetworkConnection } from 'hooks/listeners'

import { useTransactionsHistory, useUpdateTransactionAndWait } from 'state/user/hooks'

import { errors } from 'constants/errors'
import { MAD_ASSEMBLY } from 'constants/addresses'
import { etherNumbers } from 'constants/variables'
import { TxStatuses, TxTypes } from 'constants/transaction'

import { Icon, Loader, Notification } from 'components/ui'
import { createModal, ModalName } from 'modal'

import { useCallNotification } from 'notifications/hooks'
import { NotificationTypes } from 'notifications/service'

import { ConfirmationStatuses, ConfirmationStep } from './types'

import { useStep } from './useStep'
import { Approve } from './Approve'
import { ActionButton } from './ActionButton'
import { useGetModalConfigurations } from './useGetModalConfiguration'

import { CloseButton, Container, InfoTextSmall, Wrap, WrapContent } from './Style'

type Props = {
  token: Token
  approveAmount: bigint
  onCurrentClose?: VoidFunction
}

export function ApproveTx({ onCurrentClose, token, approveAmount }: Props) {
  const [txHash, setTxHash] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const chainId = useChainId()
  const { address } = useAccount()

  const isOnline = useNetworkConnection()

  const approve = useApproveERC20()
  const callNotification = useCallNotification()
  const updateTransactionAndWait = useUpdateTransactionAndWait()

  const transactions = useTransactionsHistory()
  const getModalConfigurations = useGetModalConfigurations()

  const { steps, actualStep, changeStepStatus, setupSteps, goNextStep } = useStep()
  const stepTransaction = useMemo(() => transactions?.[txHash as Hash], [transactions, txHash])
  const isPendingTx = useMemo(() => stepTransaction?.status === TxStatuses.PENDING, [stepTransaction])

  const getData = useCallback(async () => {
    if (!token) {
      throw new Error(errors.validation.TOKEN_IS_REQUIRED)
    }
    const result = await getModalConfigurations(token.address)

    if (!steps) {
      setupSteps(result.steps)
    }

    return result
  }, [getModalConfigurations, token?.address])

  const configureData = useRequest(getData)

  useEffect(() => {
    configureData.call()
  }, [])

  const handleChangeAllowance = async (amount = etherNumbers.MAX_UINT) => {
    const _txHash = await approve(amount, token.address, MAD_ASSEMBLY[chainId])

    if (_txHash) {
      setTxHash(_txHash)
      await saveApproveTx(_txHash, amount === etherNumbers.BG_ZERO ? TxTypes.REVOKE : TxTypes.APPROVE)
    }

    changeStepStatus(actualStep ?? null, ConfirmationStatuses.COMPLETE)
  }

  const handleApprove = async () => {
    changeStepStatus(actualStep ?? null, ConfirmationStatuses.PENDING)
    await handleChangeAllowance(approveAmount)
  }

  const handleRevoke = async () => {
    changeStepStatus(actualStep ?? null, ConfirmationStatuses.PENDING)
    await handleChangeAllowance(etherNumbers.BG_ZERO)
  }

  const action = useMemo(() => {
    if (actualStep === ConfirmationStep.REVOKE) {
      return handleRevoke
    }

    return handleApprove
  }, [actualStep])

  const actionRequest = useRequest(action)

  const onNextStep = () => {
    goNextStep()
    setTxHash('')
    setErrorMessage('')
  }

  const errorHandler = (errorMessage: string) => {
    setErrorMessage(errorMessage)
    if (errorMessage) {
      callNotification({
        type: NotificationTypes.ERROR,
        message: errorMessage,
      })
      changeStepStatus(actualStep ?? null, ConfirmationStatuses.FAILED)
    }
  }

  const saveApproveTx = async (txHash: Hash, txType: TxTypes) => {
    try {
      await updateTransactionAndWait({
        txHash,
        chainId,
        value: '0',
        // ToDo fix
        type: txType,
        to: token.address,
        from: address as Hash,
      })
      callNotification({
        type: NotificationTypes.SUCCESS,
        message: 'Transaction successfully sent',
      })
    } catch (err) {
      errorHandler(err.message)
    }
  }

  useEffect(() => {
    errorHandler(actionRequest.errorMessage)
  }, [actionRequest.errorMessage])

  useEffect(() => {
    if (actualStep) {
      actionRequest.call()
    }
  }, [actualStep])

  const handleRetry = async () => {
    setErrorMessage('')
    await actionRequest.call()
  }

  useEffect(() => {
    if (actionRequest.isDone && actualStep === ConfirmationStep.APPROVE) {
      setTimeout(() => {
        onCurrentClose?.()
      }, 5000)
    }
  }, [actionRequest.isDone])

  const isLoading = useMemo(() => {
    return isPendingTx || actionRequest.isLoading
  }, [isPendingTx, actionRequest.isLoading, actualStep])

  const isDisabled = useMemo(() => {
    return isPendingTx || actionRequest.isLoading
  }, [isPendingTx, actionRequest.isLoading, actualStep])

  const loadingText = useMemo(() => {
    if (isPendingTx) {
      return '_pending'
    }

    return '_processing'
  }, [isLoading, isPendingTx, actionRequest.isLoading])

  const _step = useMemo(() => (steps && actualStep ? steps[actualStep] : null), [steps, actualStep])

  return (
    <Wrap>
      <Container>
        {!isOnline && (
          <Notification type='error'>
            No&nbsp;internet connection!
            <br />
            Please check your internet connection.
          </Notification>
        )}

        {!actualStep && (
          <WrapContent>
            <Loader size={7.2} />
            Check allowance
          </WrapContent>
        )}

        {actualStep === ConfirmationStep.REVOKE && (
          <Approve txHash={txHash} errorMessage={errorMessage} isSent={actionRequest.isDone} txType={TxTypes.REVOKE}>
            <InfoTextSmall>
              In&nbsp;order to&nbsp;increase permission for your token, you need to&nbsp;revoke the current permission
              of&nbsp;_mad-assembly to&nbsp;0. This action will generate 2&nbsp;transactions (revoke and new approve).
            </InfoTextSmall>
          </Approve>
        )}

        {actualStep === ConfirmationStep.APPROVE && (
          <Approve txHash={txHash} errorMessage={errorMessage} isSent={actionRequest.isDone} txType={TxTypes.APPROVE} />
        )}
      </Container>

      <ActionButton
        step={_step}
        onRetry={handleRetry}
        isLoading={isLoading}
        onNextStep={onNextStep}
        isDisabled={isDisabled}
        loadingText={loadingText}
        onAction={actionRequest.call}
        isError={Boolean(errorMessage)}
        onCurrentClose={onCurrentClose}
      />

      <CloseButton onClick={onCurrentClose}>
        <Icon name='close' size='large' />
      </CloseButton>
    </Wrap>
  )
}

const newApproveTxModal = (props: Props) =>
  createModal({
    title: '_approve-allowance',
    align: 'center',
    name: ModalName.APPROVE_TX,
    disableOverlayClose: true,
    // @ts-ignore
    container: <ApproveTx {...props} />,
  })

export { newApproveTxModal }
